/**
 * Single source of truth for the user's preferred salon.
 *
 * Both the Book picker and the Call picker share one preference, so
 * "remember my choice" in either modal is the same slot. We keep the
 * historical key `helin.preferredSalon` and migrate any legacy
 * `helin.preferredCallSalon` value into it once on load.
 */

const STORAGE_KEY = 'helin.preferredSalon'
const LEGACY_CALL_KEY = 'helin.preferredCallSalon'
const EVENT_NAME = 'helin:preferred-salon-changed'

type Listener = (id: string | null) => void
const listeners = new Set<Listener>()

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // ignore (private mode, quota, etc.)
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

let migrated = false
function migrateOnce(): void {
  if (migrated) return
  migrated = true
  const legacy = safeGet(LEGACY_CALL_KEY)
  if (legacy && !safeGet(STORAGE_KEY)) {
    safeSet(STORAGE_KEY, legacy)
  }
  if (legacy) safeRemove(LEGACY_CALL_KEY)
}

export function readPreferredSalonId(): string | null {
  migrateOnce()
  return safeGet(STORAGE_KEY)
}

export function writePreferredSalonId(id: string): void {
  safeSet(STORAGE_KEY, id)
  notify(id)
}

export function clearPreferredSalonId(): void {
  safeRemove(STORAGE_KEY)
  notify(null)
}

export function subscribePreferredSalon(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function notify(id: string | null): void {
  listeners.forEach((cb) => {
    try {
      cb(id)
    } catch {
      // ignore listener errors so one bad listener doesn't break the rest
    }
  })
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { id } }))
  }
}

// Cross-tab sync: another tab writing the key fires a `storage` event
// in this tab. Forward that to listeners so both stores update.
// Guarded for the build-time prerender render, where `window` is absent.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return
    notify(e.newValue)
  })
}

/** Test-only: re-arm the one-shot migration so each test can verify it. */
export function __resetMigrationForTests(): void {
  migrated = false
}

/** Test-only: drop all listeners (use sparingly - store subscriptions die). */
export function __clearListenersForTests(): void {
  listeners.clear()
}
